/**
 * useClipPublishRecords.ts
 *
 * Reads a video's historical PublishRecords from `PublishRecordsStore` (a
 * session cache that fetches from the CDA once per videoId and dedupes
 * concurrent callers), then overlays live updates from `PublishTrackingStore`
 * (which receives real-time pushes via the global subscription). The merge
 * ensures:
 *   - Existing records are patched with fresher tracking-store data.
 *   - New records (dispatched after the fetch) are prepended.
 *   - Result is sorted by `publishedAt` descending.
 *
 * Because the component is wrapped in `observer`, any MobX-observable read from
 * either store triggers a re-render — so both the initial fetch landing and
 * in-flight status updates appear without polling or manual refresh.
 */

import { usePublishRecordService } from '@src/hooks/usePublishRecordService';
import {
  usePublishRecordsStore,
  usePublishTrackingStore,
} from '@src/stores-v2/StoreContext';
import { PublishStatus } from '@src/types.ts/publishRecord';
import { VideoResolution } from '@src/types.ts/video';
import { useEffect } from 'react';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { PublishRecord } from '@src/types.ts/publishRecord';
import type { Video } from '@src/types.ts/story';

interface UseClipPublishRecordsReturn {
  records: PublishRecord[];
  loading: boolean;
}

export function useClipPublishRecords(
  videoId: string | undefined,
  adapter: Partial<IVideoCreatorStore> | null,
): UseClipPublishRecordsReturn {
  const publishRecordService = usePublishRecordService();
  const publishTrackingStore = usePublishTrackingStore();
  const publishRecordsStore = usePublishRecordsStore();

  useEffect(() => {
    if (!videoId) return;
    void publishRecordsStore.ensure(videoId, publishRecordService);
  }, [videoId, publishRecordsStore, publishRecordService]);

  // Read the cache reactively: `undefined` until the fetch lands (⇒ loading),
  // then the fetched array (possibly empty). The store owns dedupe + the
  // per-session lifetime; the modal clears each videoId on close.
  const cachedForVideo = videoId
    ? publishRecordsStore.getRecords(videoId)
    : undefined;
  const fetched = cachedForVideo ?? [];
  const loading = !!videoId && cachedForVideo === undefined;

  // Merge: start with fetched historical records, overlay tracking store entries
  // for this videoId. Tracking store wins on ID conflict (fresher status).
  // NOT memoized — MobX observable reads must happen in the render path of an
  // `observer` component to be reactive. The iteration is lightweight (small N).
  const merged = new Map<string, PublishRecord>();
  for (const r of fetched) {
    merged.set(r.id, r);
  }
  if (videoId) {
    for (const [, record] of publishTrackingStore.records) {
      if (record.videoId === videoId) {
        merged.set(record.id, record);
      }
    }
  }
  const records = Array.from(merged.values()).sort((a, b) => {
    const aTime = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
    const bTime = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
    return bTime - aTime;
  });

  // Synthesize a legacy record when no real records exist but the Video has
  // evidence of a prior render (videoFilePrimary). Not cached — real records
  // take over on next publish.
  if (records.length === 0 && !loading && videoId && adapter) {
    const storyId = adapter.story?.id;
    if (storyId) {
      const video = findVideoInStore(adapter, videoId);
      if (video) {
        const synthetic = synthesizeLegacyRecord(video, storyId);
        if (synthetic) return { records: [synthetic], loading };
      }
    }
  }

  return { records, loading };
}

function findVideoInStore(
  creatorStore: Partial<IVideoCreatorStore>,
  videoId: string,
) {
  return (
    (creatorStore.currentVideo?.id === videoId
      ? creatorStore.currentVideo
      : null) ?? creatorStore.story?.otherVideos?.find(v => v.id === videoId)
  );
}

// ---------------------------------------------------------------------------
// Legacy synthesis
// ---------------------------------------------------------------------------

/** Map legacy pixel-based resolution strings to VideoResolution enum values. */
const LEGACY_RESOLUTION_MAP: Record<string, VideoResolution> = {
  '1080p': VideoResolution.HD,
  '720p': VideoResolution.High,
  '480p': VideoResolution.Medium,
  '270p': VideoResolution.Low,
};

function fromLegacyResolution(raw: string | undefined): string {
  return raw
    ? (LEGACY_RESOLUTION_MAP[raw.toLowerCase()] ?? raw)
    : VideoResolution.High;
}

/**
 * Mint a synthetic PublishRecord from legacy Video fields so pre-epic clips
 * appear in the Downloads tab. The synthetic record is never persisted; it
 * exists only in this hook's return value.
 */
function synthesizeLegacyRecord(
  video: Pick<
    Video,
    | 'id'
    | 'videoFilePrimary'
    | 'lastPublishJson'
    | 'renderId'
    | 'aspectRatio'
    | '_publishedAt'
  >,
  storyId: string,
): PublishRecord | null {
  if (!video.id || !video.videoFilePrimary) return null;
  const vfp = video.videoFilePrimary;
  return {
    id: `legacy-${video.id}`,
    videoId: video.id,
    storyId,
    groupId: `legacy-${video.id}`,
    mirrorsToLegacy: true,
    language: 'en',
    aspectRatio: video.lastPublishJson?.aspectRatio ?? video.aspectRatio,
    resolution: fromLegacyResolution(video.lastPublishJson?.resolution),
    publishStatus: PublishStatus.Succeeded,
    renderId: video.renderId ?? null,
    attempts: [],
    errorCode: null,
    errorMessage: null,
    renderStartedAt: null,
    renderCompletedAt: null,
    outputJson: {
      video: {
        // For synthetic legacy records, fileKey holds the direct download URL
        // (videoFilePrimary.url). The download hook detects the `legacy-` prefix
        // and uses it as-is instead of calling requestPublishRecordResources.
        fileKey: vfp.url,
        fileName: '',
        md5Hash: '',
        width: vfp.width,
        height: vfp.height,
        durationMs: vfp.video?.duration
          ? Math.round(vfp.video.duration * 1000)
          : undefined,
      },
    },
    snapshotRef: null,
    fingerprintsJson: null,
    publishedBy: video.lastPublishJson?.publisher ?? null,
    publishedAt: video.lastPublishJson?.timestamp ?? video._publishedAt ?? null,
    renderSourceKey: null,
  };
}
